/**
 * InstallBanner — iter125d.
 *
 * Compact, dismissible install nudge — designed to slot into post-signup /
 * waiting-for-approval / mid-flow screens where the full `InstallCTASection`
 * would be too loud.
 *
 * Auto-hides if:
 *   - the user is already in standalone (PWA installed)
 *   - the user dismissed it previously (localStorage 'install_banner_dismissed')
 *
 * Tapping the primary action:
 *   - Android Chrome → calls `beforeinstallprompt` for native install
 *   - iPhone Safari → expands inline 4-step Add-to-Home-Screen instructions
 *   - everywhere else → expands generic Chrome-on-phone hint
 *
 * Props:
 *   - `tone`: 'subtle' (default, low-key card) | 'hero' (high-contrast pop)
 *   - `dismissible`: boolean (default true)
 *   - `storageKey`: distinct dismissal key so different placements stay independent
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Share2, Smartphone, X, PlusSquare, Apple } from 'lucide-react';
import { Button } from './ui/button';

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );
}

export default function InstallBanner({
  tone = 'subtle',
  dismissible = true,
  storageKey = 'install_banner_dismissed',
  title = 'Want it on your home screen?',
  subtitle = 'Pin SplitEasy like a native app — works offline, push notifications, no app store.',
  testId = 'install-banner',
}) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const ios = isIOS();

  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey) === '1') setDismissed(true);
    } catch { /* ignore */ }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const installedHandler = () => setInstalled(true);
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [storageKey]);

  if (installed || dismissed) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    setExpanded((v) => !v);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    setDismissed(true);
  };

  const wrapperCls = tone === 'hero'
    ? 'rounded-2xl border-2 border-primary/50 bg-primary/5 p-4 shadow-md'
    : 'rounded-xl border border-primary/30 bg-primary/5 p-3';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.25 }}
        className={wrapperCls}
        data-testid={testId}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
            {ios ? <Apple className="w-4 h-4 text-primary" /> : <Smartphone className="w-4 h-4 text-primary" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm leading-tight">{title}</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{subtitle}</p>
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={handleDismiss}
              className="p-1 text-muted-foreground hover:text-foreground shrink-0"
              aria-label="Dismiss"
              data-testid={`${testId}-dismiss`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Button
            onClick={handleInstall}
            size="sm"
            className="rounded-full text-xs flex-1"
            data-testid={`${testId}-install`}
          >
            {ios ? <Share2 className="w-3.5 h-3.5 mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
            {ios ? 'How to install on iPhone' : (deferredPrompt ? 'Install on Android' : 'See how')}
          </Button>
        </div>
        {expanded && (
          <motion.ol
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.2 }}
            className="mt-2 text-xs space-y-1 text-muted-foreground"
            data-testid={`${testId}-steps`}
          >
            {ios ? (
              <>
                <li className="inline-flex gap-1.5"><span className="font-bold">1.</span><span>Open this page in <strong>Safari</strong>.</span></li>
                <li className="inline-flex gap-1.5"><span className="font-bold">2.</span><span>Tap the <Share2 className="inline w-3 h-3" /> <strong>Share</strong> button in the bottom bar.</span></li>
                <li className="inline-flex gap-1.5"><span className="font-bold">3.</span><span>Scroll → tap <PlusSquare className="inline w-3 h-3" /> <strong>Add to Home Screen</strong> → <strong>Add</strong>.</span></li>
              </>
            ) : (
              <>
                <li className="inline-flex gap-1.5"><span className="font-bold">1.</span><span>Open this page in <strong>Chrome</strong> on Android.</span></li>
                <li className="inline-flex gap-1.5"><span className="font-bold">2.</span><span>Tap the <strong>⋮ menu</strong> → <strong>Install app</strong>.</span></li>
                <li className="inline-flex gap-1.5"><span className="font-bold">3.</span><span>Confirm — SplitEasy lives on your home screen.</span></li>
              </>
            )}
          </motion.ol>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
